/* tslint:disable:no-inferrable-types */

import { LoggerImp } from './logger.imp';

/** @enum LoggerLevel verbosity level */
export enum LoggerLevel {
  ALL   = 0,
  DEBUG = 1,
  INFO  = 2,
  WARN  = 3,
  ERROR = 4,
  FATAL = 5,
  OFF   = 6
}

/**
 *
 * @class Logger
 *
 * @brief a simple/extensible logging service we can use throughout our application.  This logger
 * is a singleton.
 *
 * @link https://www.codemag.com/Article/1711021/Logging-in-Angular-Applications
 * @link https://basarat.gitbooks.io/typescript/docs/tips/singleton.html
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class Logger {

  /** @var instance LoggerImp the global logger we're using */
  private static instance: LoggerImp   = null;

  /** @var logLevel LoggerLevel the current verbosity level */
  private static logLevel: LoggerLevel = LoggerLevel.ALL;

  /** @var logWithData boolean include date stamp (or not) */
  private static logWithDate: boolean  = true;

  /** @brief set the new logging level */
  public static setVerbosity(newLevel: LoggerLevel) {

    Logger.logLevel = newLevel;
  }

  /** @brief fetch logging level */
  public static verbosity(): LoggerLevel {

    return Logger.logLevel;
  }

  /** @brief enable/disable date prefix */
  public static enableDatePrefix(flag: boolean) {

    Logger.logWithDate = flag;
  }

  /** @brief fetch logging level */
  public static datePrefix(): boolean {

    return Logger.logWithDate;
  }

  /** @brief convert a logging level to a string */
  public static levelAsString(level: LoggerLevel): string {

    switch (level) {
      case LoggerLevel.ALL:   return 'ALL';
      case LoggerLevel.DEBUG: return 'DEBUG';
      case LoggerLevel.INFO:  return 'INFO';
      case LoggerLevel.WARN:  return 'WARN';
      case LoggerLevel.ERROR: return 'ERROR';
      case LoggerLevel.FATAL: return 'FATAL';
      case LoggerLevel.OFF:   return 'OFF';
    }
  }

  /** @brief convenience for logging a message */
  public static debug(prefix: string, ...optionalParams: any[]) {

    optionalParams.unshift(prefix + '');
    Logger.log(LoggerLevel.DEBUG, optionalParams);
  }

  /** @brief convenience for logging a message */
  public static info(prefix: string, ...optionalParams: any[]) {

    optionalParams.unshift(prefix + '');
    Logger.log(LoggerLevel.INFO, optionalParams);
  }

  /** @brief convenience for logging a message */
  public static warning(prefix: string, ...optionalParams: any[]) {

    optionalParams.unshift(prefix + '');
    Logger.log(LoggerLevel.WARN, optionalParams);
  }

  /** @brief convenience for logging a message */
  public static error(prefix: string, ...optionalParams: any[]) {

    optionalParams.unshift(prefix + '');
    Logger.log(LoggerLevel.ERROR, optionalParams);
  }

  /** @brief convenience for logging a message */
  public static fatal(prefix: string, ...optionalParams: any[]) {

    optionalParams.unshift(prefix + '');
    Logger.log(LoggerLevel.FATAL, optionalParams);
  }

  /** @brief fetch the sigleton */
  public static getInstance() {

    if (!Logger.instance) {

      Logger.instance = new LoggerImp();

    }

    return Logger.instance;
  }

  /** @brief check if level is enabled by verbosity level */
  private static shouldLog(level: LoggerLevel): boolean {

    let ret: boolean = false;

    if (((level >= Logger.logLevel) && (level !== LoggerLevel.OFF)) ||
      (Logger.logLevel === LoggerLevel.ALL)) {

      ret = true;
    }

    return ret;
  }

  /** @brief log a message at some level of verbosity */
  public static log(level: LoggerLevel, ...args: any[]): boolean {

    if (!Logger.shouldLog(level)) {

      return false;
    }

    const logger: LoggerImp  = this.getInstance();
    let dateString: string = '';

    if (Logger.datePrefix()) {

      const date: Date = new Date();

      dateString += date.getFullYear() + '-' + date.getMonth() + '-' + date.getDay() + ' ';
      dateString += date.getHours() + ':' + date.getMinutes() + ':' + date.getSeconds();

    }

    let params: any[] = [];

    let leader: string = '';

    if (dateString.length > 0) {

      leader = dateString + ' ' + Logger.levelAsString(level);

    } else {

      leader = Logger.levelAsString(level);
    }

    params.push(leader);

    params = params.concat(...args);

    if (params.length >= 1) {

      if (typeof params[0] === 'string') {

        /* if the first item is a string, color it by log level */

        const arg1: string = `%c%s`;
        let   arg2: string = 'color: white;';
        const arg3: string = params[0];

        switch (level) {
          case LoggerLevel.ALL:   {arg2 = 'color: white;';          break;}
          case LoggerLevel.DEBUG: {arg2 = 'color: limegreen;';      break;}
          case LoggerLevel.INFO:  {arg2 = 'color: lightsteelblue;'; break;}
          case LoggerLevel.WARN:  {arg2 = 'color: yellow;';         break;}
          case LoggerLevel.ERROR: {arg2 = 'color: orangered;';      break;}
          case LoggerLevel.FATAL: {arg2 = 'color: red;';            break;}
        }

        params.shift();
        params.unshift(arg1, arg2, arg3);
      }
    }

    logger.log.apply(logger, params);

    /* all done */

    return true;
  }

}
